import React from 'react';
import {
    Alert,
    Box,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material';
import {Link as RouterLink} from 'react-router';
import TabEmpty from './TabEmpty';
import TabLoading from './TabLoading';

function LinkList({items, emptyLabel}) {
    if (!items.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyLabel}
            </Typography>
        );
    }

    return (
        <Stack spacing={0.75}>
            {items.map((item, idx) => {
                const key = item.id || item.href || `${idx}`;
                const title = item.title || item.icrNumber || item.id || 'Untitled';
                const meta = item.icrNumber && item.icrNumber !== title ? item.icrNumber : null;
                const linkBody = (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" component="span">
                            {title}
                        </Typography>
                        {meta ? (
                            <Typography variant="caption" color="text.secondary" component="span">
                                {meta}
                            </Typography>
                        ) : null}
                    </Stack>
                );

                if (item.href) {
                    return (
                        <MuiLink
                            key={key}
                            component={RouterLink}
                            to={item.href}
                            underline="hover"
                            color="inherit"
                        >
                            {linkBody}
                        </MuiLink>
                    );
                }

                return <Box key={key}>{linkBody}</Box>;
            })}
        </Stack>
    );
}

function LinkSection({title, items, emptyLabel}) {
    return (
        <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
                {title} ({items.length})
            </Typography>
            <LinkList items={items} emptyLabel={emptyLabel}/>
        </Stack>
    );
}

/**
 * Links tab — ICRs in Products and charts containing this entity.
 *
 * @param {Object} props
 * @param {Array} [props.icrs]
 * @param {Array} [props.charts]
 * @param {boolean} props.loading
 * @param {Error|null} [props.error]
 */
const EntityLinksTab = ({
    icrs = [],
    charts = [],
    loading,
    error = null,
}) => {
    if (loading) return <TabLoading/>;

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">
                    {error.message || 'Failed to load entity links.'}
                </Alert>
            </Box>
        );
    }

    const total = icrs.length + charts.length;
    if (!total) {
        return (
            <TabEmpty message="This entity is not linked to any ICRs or other charts."/>
        );
    }

    return (
        <Stack spacing={3} px={2} py={2}>
            <LinkSection
                title="In Products"
                items={icrs}
                emptyLabel="None"
            />
            <LinkSection
                title="Charts"
                items={charts}
                emptyLabel="None"
            />
        </Stack>
    );
};

export default EntityLinksTab;
